import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { FC } from 'react'
import { Divider, SkeletonText } from 'utoniq-ui'

import {
  Maybe,
  RecommendCollectionFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styleSets } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, Template } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  items: Maybe<{ edges: { node: RecommendCollectionFragment }[] }>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const RecommendCollectionsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('recommendCollections')
  const { routerNames } = useRouter()

  return (
    <ArtistThemeProvider>
      <Head title={routerNames.RecommendCollections} />

      <BaseLayout
        margin
        header={{
          back: true,
          title: t('title'),
        }}
      >
        <ScrollFetcher
          nodes={state.items?.edges.map(({ node }, index) => (
            <RecommendCollectionItem
              key={node.id}
              recentlyViewdCollection={node}
              divider={
                !!state.items?.edges.length &&
                index < state.items?.edges.length - 1
              }
              conductorName="recentryViewCollectionList"
            />
          ))}
          componentProps={{ spacing: 4 }}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          emptyMessage={t('emptyMessage')}
          loadingComponent={
            <Stack spacing={4} sx={{ pt: 2 }}>
              {Array.from(Array(5).keys()).map(index => (
                <RecommendCollectionItemSkeleton
                  key={index}
                  divider={index < 4}
                />
              ))}
            </Stack>
          }
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type RecommendCollectionItemProps = {
  recentlyViewdCollection: RecommendCollectionFragment
  divider: boolean
  /** analyticsクリックイベント時の導線名 */
  conductorName: string
}
const RecommendCollectionItem: FC<RecommendCollectionItemProps> = ({
  recentlyViewdCollection,
  divider,
  conductorName,
}) => {
  const { routerPages, generatePath } = useRouter()
  const { analytics } = useAnalytics()
  const thumbnailUri =
    recentlyViewdCollection.collection.thumbnail.mediumUri ??
    recentlyViewdCollection.collection.thumbnail.uri ??
    ''
  return (
    <Card elevation={0} sx={{ bgcolor: 'transparent', borderRadius: 0 }}>
      <CardActionArea
        href={generatePath(routerPages.StoreCollectionDetail, {
          artistId: recentlyViewdCollection.collection.artistId,
          collectionId: recentlyViewdCollection.collection.id,
        })}
        onClick={() => {
          analytics?.track('click_select', {
            applicationKey: config.applicationKey,
            artistId: recentlyViewdCollection.collection.artistId,
            collectionId: recentlyViewdCollection.collection.id,
            name: conductorName,
          })
        }}
      >
        <Stack direction="row" spacing={4}>
          <Avatar
            variant="square"
            src={thumbnailUri}
            sx={{ height: 60, width: 107, borderRadius: 1 }}
          />
          <Stack spacing={1} py={2} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={styleSets.lineCamp(1)}>
              {recentlyViewdCollection.collection.title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={styleSets.lineCamp(1)}
            >
              {recentlyViewdCollection.collection.description}
            </Typography>
          </Stack>
        </Stack>
      </CardActionArea>
      {divider && <Divider sx={{ mt: 4 }} />}
    </Card>
  )
}

const RecommendCollectionItemSkeleton: Cell<{ divider: boolean }> = ({
  divider,
}) => (
  <Stack>
    <Stack flex={1} direction="row" alignItems="center" spacing={4}>
      <Box>
        <Skeleton
          variant="rectangular"
          width={107}
          height={60}
          sx={{ borderRadius: 1 }}
        />
      </Box>
      <Stack flex={1}>
        <SkeletonText variant="medium" width="45%" />
        <SkeletonText variant="medium" width="100%" />
      </Stack>
    </Stack>
    {divider && <Divider sx={{ mt: 4 }} />}
  </Stack>
)
